import Analytics from '@/utils/analytics';
import React, { useRef } from 'react';
import { APP_CONTAINER_STACK_ROUTES } from './routes';
import { AppContainerStackParams } from './types';
import { ContainerStack } from './container-stack';
import {
  createNavigatorFactory,
  DefaultTheme,
  NavigationContainer,
  NavigationContainerRef,
  NavigationHelpersContext,
  StackRouter,
  useNavigationBuilder,
} from '@react-navigation/native';
import { ErrorBoundary } from '@/components/error-boundary';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { ExperiencePopStateSync } from '@/components/experience-popstate-sync';
import { FINANCE_HUB_PATH } from '@finance-hub/finance-hub-route-config';
import { FinanceHubStack } from '@finance-hub/finance-hub-stack';
import { getActiveRouteName, isWeb } from '@/utils/navigation';
import { isBootStorefrontIntentPending } from '@domain/microproducts/boot-params';
import { partnerSupplier } from '@/utils/environment';
import { SALES_PORTAL_PATH } from '@sales-portal/sales-portal-route-config';
import { SalesPortalStack } from '@sales-portal/sales-portal-stack';
import { sentryNavigationIntegration } from '@/utils/sentry';
import { SmartOrderContainerStack } from './smart-order-container-stack';
import { SmartOrderUserContextProvider } from '@/contexts/smart-order-user-context';
import { UserContextProvider } from '@/contexts/user-context';
import { UserOverridesContextProvider } from '@/contexts/user-overrides-context';
import { useSentryLaunchTracking } from '@/hooks/use-sentry-launch-tracking';
import { useSmartOrderParams } from '@/hooks/use-smart-order-params';
import { useTheme } from '@/contexts/theme-context';

// iOS 12 has rendering problems when elements are deeply nested, so this
// navigator only renders its initial route instead of a number of
// unnecessary components like StackNavigator
const Pure = createNavigatorFactory(({ children }) => {
  const { descriptors, navigation, state } = useNavigationBuilder(StackRouter, {
    children,
    screenOptions: {
      title: partnerSupplier.fullName,
    },
  });

  return (
    <NavigationHelpersContext.Provider value={navigation}>
      {descriptors[state.routes[state.index].key].render()}
    </NavigationHelpersContext.Provider>
  );
})();

const ContainerComponent: React.FC = () => {
  const smartOrderParams = useSmartOrderParams();
  const { mode, switchToExperience } = useExperienceStore();

  const isSalesPortalRoute =
    isWeb &&
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith(SALES_PORTAL_PATH);

  const isFinanceHubRoute =
    isWeb &&
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith(FINANCE_HUB_PATH);

  // A /storefront/:productId entry URL is an operator-portal deep link
  // (boot-params captures it once at module evaluation). While the intent
  // is pending — not yet consumed by the post-auth latch in app-stack — it
  // must defeat a persisted SALES_PORTAL experience mode, which zustand
  // rehydrates asynchronously and would otherwise swallow the link. The
  // forcing is TRANSIENT on purpose: it never writes the store, so a user
  // who turns out to be unauthorized for the operator portal (the latch
  // then never mounts and nothing can consume the intent) keeps their
  // saved preference for the next boot. The store is only rewritten when
  // the intent is actually applied, by the post-auth latch
  // (use-storefront-boot-intent). Inert (always false) on native via the
  // boot-params platform split.
  const isPendingStorefrontBootRoute = isWeb && isBootStorefrontIntentPending();

  if (
    (mode === ExperienceMode.SALES_PORTAL || isSalesPortalRoute) &&
    !isPendingStorefrontBootRoute
  ) {
    // early return for sales portal experience or if the user routes directly to the sales portal
    if (mode !== ExperienceMode.SALES_PORTAL) {
      switchToExperience(ExperienceMode.SALES_PORTAL);
    }
    return <SalesPortalStack />;
  }

  if (mode === ExperienceMode.FINANCE_HUB || isFinanceHubRoute) {
    if (mode !== ExperienceMode.FINANCE_HUB) {
      switchToExperience(ExperienceMode.FINANCE_HUB);
    }
    return <FinanceHubStack />;
  }

  // Smart order flow
  if (smartOrderParams) {
    return (
      <SmartOrderUserContextProvider smartOrderParams={smartOrderParams}>
        <SmartOrderContainerStack />
      </SmartOrderUserContextProvider>
    );
  }

  // default operator portal experience
  return (
    <UserOverridesContextProvider>
      <UserContextProvider>
        <ContainerStack />
      </UserContextProvider>
    </UserOverridesContextProvider>
  );
};

export const AppContainer: React.FC = () => {
  const navigationRef =
    useRef<NavigationContainerRef<AppContainerStackParams> | null>(null);
  const theme = useTheme();
  const routeNameRef = useRef<string>(undefined);
  const { handleNavigationReady, handleNavigationStateChange } =
    useSentryLaunchTracking();

  return (
    <ErrorBoundary>
      <ExperiencePopStateSync />
      <NavigationContainer
        onReady={() => {
          if (navigationRef.current) {
            sentryNavigationIntegration.registerNavigationContainer(
              navigationRef.current,
            );
          }
          handleNavigationReady();
        }}
        onStateChange={(state) => {
          if (!state) {
            return;
          }

          const routeName = getActiveRouteName(state);
          if (routeName !== routeNameRef.current) {
            Analytics.screen(routeName, routeNameRef.current);
          }

          routeNameRef.current = routeName;

          handleNavigationStateChange(state);
        }}
        ref={navigationRef}
        theme={{
          ...DefaultTheme,
          colors: {
            ...DefaultTheme.colors,
            background: theme.colors.white,
            primary: theme.colors.primary,
          },
        }}
      >
        <Pure.Navigator>
          <Pure.Screen
            component={ContainerComponent}
            name={APP_CONTAINER_STACK_ROUTES.Container}
          />
        </Pure.Navigator>
      </NavigationContainer>
    </ErrorBoundary>
  );
};
